import * as React from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Save, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import DetailDrawer from "@/components/DetailDrawer";
import SiteCalendarPanel from "@/components/sites/SiteCalendarPanel";
import { toast } from "sonner";

const DEFAULT_FORM = { name: "", address: "", timezone: "Europe/London", color: "#6E3CFA" };
const TZ_OPTIONS = [
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin",
  "America/New_York", "America/Los_Angeles", "Asia/Singapore", "Australia/Sydney",
];

export default function Sites() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const [rows, setRows] = React.useState(null);
  const [active, setActive] = React.useState(null); // site being viewed/edited or "new"
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [busy, setBusy] = React.useState(false);
  const canManage = has("sites.manage");

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/companies/${companyId}/sites`);
    setRows(data.sites);
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type; if (t && t.startsWith("site.")) load(); };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  function openNew() { setActive("new"); setForm(DEFAULT_FORM); }
  function openEdit(s) {
    setActive(s);
    setForm({ name: s.name, address: s.address || "", timezone: s.timezone, color: s.color });
  }
  function close() { setActive(null); }

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      if (active === "new") {
        await api.post(`/companies/${companyId}/sites`, form);
        toast.success("Site created");
      } else {
        await api.patch(`/companies/${companyId}/sites/${active.id}`, form);
        toast.success("Site saved");
      }
      close(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function del(s) {
    if (!window.confirm(`Archive site "${s.name}"?`)) return;
    try {
      await api.delete(`/companies/${companyId}/sites/${s.id}`);
      toast.success("Archived");
      close(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  if (!rows) return <div className="p-6 text-brand-muted text-[12px]" data-testid="sites-loading">Loading…</div>;

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="sites-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="micro-label">Locations</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Sites</h1>
          <div className="mt-1 text-[12px] text-brand-muted">
            <span className="mono-num text-brand-ink">{rows.length}</span> active
          </div>
        </div>
        {canManage && (
          <button className="btn-violet" onClick={openNew} data-testid="site-new">
            <Plus size={13} /> New site
          </button>
        )}
      </div>

      <div className="mt-4 card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="sites-table">
          <thead>
            <tr>
              <th style={{width: "34%"}}>Name</th>
              <th>Address</th>
              <th style={{width: "16%"}}>Timezone</th>
              <th style={{width: "10%"}}>Members</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-clickable="true" onClick={() => openEdit(s)}
                  data-testid={`site-row-${s.id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded-full" style={{background: s.color}} />
                    <span className="font-medium text-brand-ink">{s.name}</span>
                  </div>
                </td>
                <td className="text-[12px] text-brand-muted">{s.address || "—"}</td>
                <td className="mono-num text-[11.5px] text-brand-muted">{s.timezone}</td>
                <td className="mono-num text-brand-ink">{s.member_count}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <DetailDrawer
        open={!!active}
        onOpenChange={(v) => { if (!v) close(); }}
        title={active === "new" ? "New site" : (active?.name || "Site")}
        subtitle={active === "new" ? "Create a location" : "Edit site"}
        testid="site-drawer"
      >
        {active && (
          <SiteDrawerBody active={active} form={form} setForm={setForm} busy={busy}
                          canManage={canManage} save={save} del={del}
                          companyId={companyId} />
        )}
      </DetailDrawer>
    </div>
  );
}

function SiteDrawerBody({ active, form, setForm, busy, canManage, save, del, companyId }) {
  const [tab, setTab] = React.useState("info");
  const isNew = active === "new";
  return (
    <div>
      {!isNew && (
        <div className="flex items-center gap-1 px-5 pt-3 border-b border-brand-rule">
          <SiteTab v="info" cur={tab} setV={setTab}>Info</SiteTab>
          <SiteTab v="calendar" cur={tab} setV={setTab}>Hours & closures</SiteTab>
        </div>
      )}
      {(isNew || tab === "info") && (
        <div className="p-5 space-y-4">
          <div>
            <label className="micro-label">Name</label>
            <input className="field-input mt-1" value={form.name}
                   onChange={(e)=>setForm(f=>({...f, name:e.target.value}))}
                   disabled={!canManage} data-testid="site-name-input" />
          </div>
          <div>
            <label className="micro-label">Address</label>
            <input className="field-input mt-1" value={form.address}
                   onChange={(e)=>setForm(f=>({...f, address:e.target.value}))}
                   disabled={!canManage} data-testid="site-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="micro-label">Timezone</label>
              <select className="field-input mt-1" value={form.timezone}
                      onChange={(e)=>setForm(f=>({...f, timezone:e.target.value}))}
                      disabled={!canManage} data-testid="site-tz-input">
                {TZ_OPTIONS.map((tz) => <option key={tz}>{tz}</option>)}
              </select>
            </div>
            <div>
              <label className="micro-label">Colour</label>
              <input type="color" className="mt-1 h-9 w-full rounded-md border border-brand-rule"
                     value={form.color}
                     onChange={(e)=>setForm(f=>({...f, color:e.target.value}))}
                     disabled={!canManage} data-testid="site-color-input" />
            </div>
          </div>
          {canManage && (
            <div className="pt-3 border-t border-brand-rule flex items-center gap-2">
              <button className="btn-violet" onClick={save} disabled={busy || !form.name.trim()}
                      data-testid="site-save">
                <Save size={13} /> {busy ? "…" : (isNew ? "Create" : "Save")}
              </button>
              {!isNew && (
                <button className="btn-outline text-brand-coral border-brand-coral/40"
                        onClick={() => del(active)} data-testid="site-archive">
                  <Trash2 size={13} /> Archive
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {!isNew && tab === "calendar" && (
        <SiteCalendarPanel companyId={companyId} siteId={active.id} canEdit={canManage} />
      )}
    </div>
  );
}

function SiteTab({ v, cur, setV, children }) {
  const active = v === cur;
  return (
    <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            onClick={() => setV(v)} data-testid={`site-drawer-tab-${v}`}>
      {children}
    </button>
  );
}
